/**
 * Auth store — Zustand. Mirrors what the gateway returns on /me, plus
 * a derived `status` for route guards.
 *
 * The store never persists tokens — they live in httpOnly cookies set
 * by the gateway. We persist only the last-known user + active
 * workspace pointer to avoid a "splash" on every reload.
 *
 * Phase Auth (2026-05-18): the legacy stub that hardcoded an
 * authenticated founder is gone. The store starts in `idle`,
 * AuthBootstrap flips it to `loading`, then `authenticated` or
 * `unauthenticated` based on the live /api/auth/me response.
 */
import type { MeResponse, Workspace, WorkspaceMembership } from '@chaos/types/auth';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthSlice {
  status: AuthStatus;
  user: MeResponse['user'] | null;
  workspaces: Array<{ workspace: Workspace; membership: WorkspaceMembership }>;
  activeWorkspaceId: string | null;

  setLoading: () => void;
  setSession: (me: MeResponse) => void;
  setUnauthenticated: () => void;
  /**
   * End the bootstrap loading state WITHOUT logging the user out — used
   * when GET /api/auth/me fails for a TRANSIENT reason (network blip /
   * 5xx / 429) after retries are exhausted. We must NOT flip to
   * `unauthenticated` (that would fire the reactive redirect to /login
   * and wipe a session whose cookie may still be perfectly valid):
   *
   *   - If a user was rehydrated from persisted state, mark
   *     `authenticated` so the shell mounts with that last-known user.
   *     The httpOnly cookie still rides every subsequent request; if the
   *     session really is dead, the first board query's 401 trips the
   *     QueryCache → setUnauthenticated() bridge (lib/api/index.ts) and
   *     the user is then sent to /login — the correct, evidence-based
   *     logout, not a logout on a transient blip.
   *   - If there is NO persisted user, stay in `loading` (the splash),
   *     NEVER `unauthenticated`, so the reactive redirect does not fire.
   *
   * Never clears the persisted user; persist/partialize stay intact.
   */
  setBootstrapDeferred: () => void;
  setActiveWorkspace: (workspaceId: string) => void;
}

export const useAuthStore = create<AuthSlice>()(
  persist(
    (set) => ({
      status: 'idle',
      user: null,
      workspaces: [],
      activeWorkspaceId: null,

      setLoading: () => set({ status: 'loading' }),
      setSession: (me) => {
        const firstWorkspaceId = me.workspaces[0]?.workspace.id ?? null;
        set((state) => ({
          status: 'authenticated',
          user: me.user,
          workspaces: me.workspaces,
          activeWorkspaceId: me.workspaces.some(
            ({ workspace }) => workspace.id === state.activeWorkspaceId,
          )
            ? state.activeWorkspaceId
            : firstWorkspaceId,
        }));
      },
      setUnauthenticated: () =>
        set({
          status: 'unauthenticated',
          user: null,
          workspaces: [],
          activeWorkspaceId: null,
        }),
      setBootstrapDeferred: () =>
        set((state) =>
          // Keep the rehydrated user; only the `status` changes. Never set
          // `unauthenticated` and never clear `user`/`workspaces`.
          state.user ? { status: 'authenticated' } : { status: 'loading' },
        ),
      setActiveWorkspace: (workspaceId) => set({ activeWorkspaceId: workspaceId }),
    }),
    {
      name: 'chaos-auth',
      // Persist only the small public bits — the cookies are the source
      // of truth for the actual access decision.
      partialize: (state) => ({
        user: state.user,
        activeWorkspaceId: state.activeWorkspaceId,
      }),
      // v2 removes persisted memberships. They are private authorization data,
      // not shell chrome, and must come only from a fresh /api/auth/me.
      version: 2,
      migrate: (persisted) => {
        const old = (persisted ?? {}) as Partial<AuthSlice>;
        return {
          user: old.user ?? null,
          activeWorkspaceId: old.activeWorkspaceId ?? null,
        };
      },
    },
  ),
);
